import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Search, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

const Districts = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [editData, setEditData] = useState({
    office_name: '',
    office_address: '',
    latitude: '',
    longitude: '',
    geo_fence_radius: 150,
  });

  const divisions = [
    'Karachi', 'Hyderabad', 'Mirpur Khas',
    'Shaheed Benazirabad', 'Sukkur', 'Larkana'
  ];

  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (divisionFilter) params.division = divisionFilter;
      const response = await api.get('/districts', { params });
      setDistricts(response.data.districts);
    } catch (err) {
      toast.error('Failed to load districts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, [search, divisionFilter]);

  const handleEdit = (district) => {
    setSelectedDistrict(district);
    setEditData({
      office_name: district.office_name || '',
      office_address: district.office_address || '',
      latitude: district.latitude || '',
      longitude: district.longitude || '',
      geo_fence_radius: district.geo_fence_radius || 150,
    });
    setShowEditModal(true);
  };

  const handleUpdateDistrict = async (e) => {
    e.preventDefault();
    try {
      if (selectedDistrict.office_id) {
        // Office exists — update it
        await api.put(`/districts/${selectedDistrict.id}/office`, {
          name: editData.office_name,
          address: editData.office_address,
          latitude: editData.latitude,
          longitude: editData.longitude,
          geo_fence_radius: editData.geo_fence_radius,
        });
      } else {
        // No office — create it
        await api.post(`/districts/${selectedDistrict.id}/office`, {
          name: editData.office_name,
          address: editData.office_address,
          latitude: editData.latitude,
          longitude: editData.longitude,
          geo_fence_radius: editData.geo_fence_radius,
        });
      }
      toast.success('District office saved successfully!');
      setShowEditModal(false);
      fetchDistricts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save district office');
    }
  };

  // Group by division
  const grouped = districts.reduce((acc, district) => {
    const div = district.division_name;
    if (!acc[div]) acc[div] = [];
    acc[div].push(district);
    return acc;
  }, {});

  return (
    <Layout title="Districts">

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search districts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Divisions</option>
          {divisions.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        {/* Summary */}
        <div className="ml-auto flex gap-3">
          {Object.keys(grouped).map(div => (
            <div key={div} className="bg-white rounded-lg shadow px-4 py-2 text-sm">
              <span className="text-gray-500">{div}:</span>
              <span className="font-bold text-blue-800 ml-1">{grouped[div].length}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Districts by Division */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading districts...</div>
      ) : (
        Object.keys(grouped).map(division => (
          <div key={division} className="mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <MapPin size={18} className="text-blue-800" />
              {division} Division
              <span className="text-sm font-normal text-gray-500">
                ({grouped[division].length} districts)
              </span>
            </h2>
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">District Name</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">District Code</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Office</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Geo-fence</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[division].map((district, index) => (
                    <tr key={district.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {district.district_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {district.district_code}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{district.office_name || '—'}</div>
                        {district.office_address && (
                          <div className="text-xs text-gray-400">{district.office_address}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {district.geo_fence_radius
                          ? `${district.geo_fence_radius}m`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {district.office_id ? (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                            Office Set
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                            No Office
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(district)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Edit Office
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Total */}
      <div className="text-sm text-gray-500 mt-2">
        Total: {districts.length} districts
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedDistrict && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">
                Edit Office — {selectedDistrict.district_name}
              </h2>
              <button onClick={() => setShowEditModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleUpdateDistrict} className="p-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Office Name</label>
                  <input type="text" value={editData.office_name}
                    onChange={(e) => setEditData({...editData, office_name: e.target.value})}
                    placeholder="e.g. BBSHRRDB Karachi East Office"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Office Address</label>
                  <input type="text" value={editData.office_address}
                    onChange={(e) => setEditData({...editData, office_address: e.target.value})}
                    placeholder="Full office address"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Latitude</label>
                  <input type="number" step="any" value={editData.latitude}
                    onChange={(e) => setEditData({...editData, latitude: e.target.value})}
                    placeholder="e.g. 24.8607"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Longitude</label>
                  <input type="number" step="any" value={editData.longitude}
                    onChange={(e) => setEditData({...editData, longitude: e.target.value})}
                    placeholder="e.g. 67.0011"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Geo-fence Radius (meters)</label>
                  <input type="number" value={editData.geo_fence_radius}
                    onChange={(e) => setEditData({...editData, geo_fence_radius: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-blue-700 font-medium">📍 How to get coordinates:</p>
                <p className="text-xs text-blue-600 mt-1">
                  Go to Google Maps → Right click on office location → Copy coordinates
                </p>
              </div>

              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowEditModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">
                  Save Office
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Districts;